import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ArrowsLeftRight, MagicWand, Check, Link, DownloadSimple, FileXls } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const ClientMatchingTab = ({ clientId, clientName }) => {
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoMatching, setAutoMatching] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [matchingInvoice, setMatchingInvoice] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [scanningAll, setScanningAll] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [txnRes, invRes] = await Promise.all([
        api.get(`/bank-transactions?client_id=${clientId}`),
        api.get(`/invoices?client_id=${clientId}`),
      ]);
      setTransactions(txnRes.data);
      setInvoices(invRes.data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);

  const unmatchedTxns = transactions.filter(t => t.status === 'unmatched');
  const pendingInvoices = invoices.filter(i => i.status === 'pending');
  const matchedTxns = transactions.filter(t => t.status === 'matched');

  const handleAutoMatch = async () => {
    setAutoMatching(true);
    try {
      const { data } = await api.post(`/auto-match/${clientId}`);
      toast.success(data.message); fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setAutoMatching(false); }
  };

  const handleScanAndMatch = async () => {
    setScanningAll(true);
    setScanResult(null);
    try {
      const { data } = await api.post(`/clients/${clientId}/scan-and-match`);
      setScanResult(data);
      toast.success(data.message);
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setScanningAll(false); }
  };

  const handleManualMatch = async () => {
    if (!selectedTransaction || !matchingInvoice) { toast.error('Select both a transaction and an invoice'); return; }
    try {
      await api.post('/match-transaction', { transaction_id: selectedTransaction, invoice_id: matchingInvoice });
      toast.success('Matched!'); setSelectedTransaction(null); setMatchingInvoice(null); fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleExport = async (endpoint) => {
    setExporting(true);
    try {
      const response = await api.get(`/export/${endpoint}?client_id=${clientId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${endpoint}_${clientName || 'client'}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Exported!');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="client-matching-tab">
      {/* Toolbar */}
      <Card className="border border-border">
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button onClick={handleScanAndMatch} disabled={scanningAll} variant="default" data-testid="scan-match-all-btn">
                {scanningAll ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Scanning & Matching...</> : <><MagicWand className="w-4 h-4 mr-2" />Scan & Match All</>}
              </Button>
              <Button variant="outline" onClick={handleAutoMatch} disabled={autoMatching || unmatchedTxns.length === 0} data-testid="auto-match-btn">
                {autoMatching ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" /> : <ArrowsLeftRight className="w-4 h-4 mr-2" />}
                Match Only
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('matched-transactions')} disabled={exporting || matchedTxns.length === 0} data-testid="export-matched-btn">
                <FileXls className="w-4 h-4 mr-1" /> Export Matched
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('all-transactions')} disabled={exporting} data-testid="export-all-btn">
                <DownloadSimple className="w-4 h-4 mr-1" /> Export All
              </Button>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            <strong>{unmatchedTxns.length}</strong> unmatched transactions | <strong>{pendingInvoices.length}</strong> pending invoices | <strong>{matchedTxns.length}</strong> matched
          </div>
          {scanResult && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm space-y-1" data-testid="scan-result">
              <p className="font-medium text-green-800">{scanResult.message}</p>
              {scanResult.steps?.map((step, i) => (
                <p key={i} className="text-green-700 text-xs">{step}</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unmatched panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="heading-4">Unmatched Transactions</CardTitle>
            <CardDescription>{unmatchedTxns.length} waiting</CardDescription>
          </CardHeader>
          <CardContent className="p-0 max-h-80 overflow-auto">
            {unmatchedTxns.length === 0 ? (
              <div className="text-center py-8"><Check className="w-8 h-8 mx-auto mb-2 text-green-500" /><p className="text-muted-foreground">All matched!</p></div>
            ) : (
              <Table>
                <TableHeader><TableRow className="table-header"><TableHead className="w-8"></TableHead><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {unmatchedTxns.map((txn) => (
                    <TableRow key={txn.id} className={`cursor-pointer ${selectedTransaction === txn.id ? 'bg-primary/10' : ''}`} onClick={() => setSelectedTransaction(txn.id)}>
                      <TableCell><input type="radio" checked={selectedTransaction === txn.id} onChange={() => {}} className="w-4 h-4" /></TableCell>
                      <TableCell className="text-sm">{txn.transaction_date || '-'}</TableCell>
                      <TableCell className="text-sm max-w-32 truncate">{txn.description || '-'}</TableCell>
                      <TableCell className={`text-sm ${txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(txn.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="heading-4">Pending Invoices</CardTitle>
            <CardDescription>{pendingInvoices.length} waiting</CardDescription>
          </CardHeader>
          <CardContent className="p-0 max-h-80 overflow-auto">
            {pendingInvoices.length === 0 ? (
              <div className="text-center py-8"><Check className="w-8 h-8 mx-auto mb-2 text-green-500" /><p className="text-muted-foreground">All matched!</p></div>
            ) : (
              <Table>
                <TableHeader><TableRow className="table-header"><TableHead className="w-8"></TableHead><TableHead>Type</TableHead><TableHead>Supplier / Number</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {pendingInvoices.map((inv) => (
                    <TableRow key={inv.id} className={`cursor-pointer ${matchingInvoice === inv.id ? 'bg-primary/10' : ''}`} onClick={() => setMatchingInvoice(inv.id)}>
                      <TableCell><input type="radio" checked={matchingInvoice === inv.id} onChange={() => {}} className="w-4 h-4" /></TableCell>
                      <TableCell><Badge variant="outline" className="capitalize text-xs">{inv.invoice_type}</Badge></TableCell>
                      <TableCell className="text-sm max-w-32 truncate">{inv.supplier_name || inv.invoice_number || '-'}</TableCell>
                      <TableCell className="text-sm">{formatCurrency(inv.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Match button */}
      {(selectedTransaction || matchingInvoice) && (
        <div className="flex justify-center">
          <Button onClick={handleManualMatch} disabled={!selectedTransaction || !matchingInvoice} className="px-8" data-testid="manual-match-btn">
            <Link className="w-4 h-4 mr-2" /> Match Selected
          </Button>
        </div>
      )}

      {/* Matched table */}
      {matchedTxns.length > 0 && (
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="heading-4 flex items-center gap-2"><ArrowsLeftRight className="w-5 h-5" /> Matched</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="table-header"><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Amount</TableHead><TableHead>Invoice</TableHead><TableHead>Confidence</TableHead></TableRow></TableHeader>
              <TableBody>
                {matchedTxns.slice(0, 20).map((txn) => {
                  const inv = invoices.find(i => i.id === txn.matched_invoice_id);
                  return (
                    <TableRow key={txn.id}>
                      <TableCell className="text-sm">{txn.transaction_date || '-'}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{txn.description || '-'}</TableCell>
                      <TableCell className="text-sm">{formatCurrency(txn.amount)}</TableCell>
                      <TableCell className="text-sm">{inv ? (inv.supplier_name || inv.invoice_number || 'Invoice') : '-'}</TableCell>
                      <TableCell>{txn.match_confidence ? <Badge variant="secondary">{Math.round(txn.match_confidence * 100)}%</Badge> : <Badge variant="secondary">Manual</Badge>}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
